// "Chez moi" — a single reusable bedroom scene. The scene KEY is always 'room'; its logical location
// (cfg.id, used for the server snapshot + prop/occupant filtering) is RETARGETED on each entry to the
// room being viewed (room:<avatarId>). Entered via scene.start (a fresh init each time), so the inherited
// drivers rebuild against the new room — no stale-driver/wake gymnastics.
//
// On top of the inherited rendering this scene owns the in-room UX: a title ("⭐ Ma chambre" / "Chez X"),
// an exit, a 💜 reaction (POST /api/room/react), and a 📖 guestbook panel (who visited / who reacted) —
// completing the visit→react→see-reactions loop that was server-only before.
import 'phaser'
import GreyboxLocationScene from './greybox/GreyboxLocationScene'
import type { LocationConfig } from './greybox/campusMaps'
import { SceneName } from './enums/SceneNames'
import ServerLink from '../net/ServerLink'

const BEDROOM_ASCII = [
    '###########',
    '#b_______p#',
    '#_________#',
    '#_________#',
    '#_________#',
    '#_________#',
    '#_________#',
    '#_________#',
    '#####D#####',
]
const BEDROOM_CFG: LocationConfig = {
    id: SceneName.Room, ascii: BEDROOM_ASCII, spawn: { x: 5, y: 6 }, doors: [], theme: 'indoors',
}

export default class RoomScene extends GreyboxLocationScene {
    private ownerName = ''
    private mine = false
    private recentVisits = 0
    private gbPanel?: Phaser.GameObjects.Container

    constructor() { super({ ...BEDROOM_CFG }) }

    init(data?: { roomId?: string; ownerName?: string; mine?: boolean; recentVisits?: number }): void {
        if (data?.roomId) this.cfg.id = data.roomId as SceneName // see header: cfg.id used only as a string
        this.ownerName = data?.ownerName || ''
        this.mine = !!data?.mine
        this.recentVisits = data?.recentVisits || 0
        this.gbPanel = undefined
    }

    create(): void {
        super.create()
        const W = this.scale.width
        // owner name: passed by the directory, else looked up from the live agent cache, else generic.
        const avatarId = this.cfg.id.slice(5)
        const name = this.ownerName || ServerLink.agents.get(avatarId)?.displayName || 'cette chambre'
        const title = this.mine ? '⭐ Ma chambre' : `Chez ${name}`

        // Title banner (top-center).
        this.add.text(W / 2, 8, title, {
            fontFamily: 'sans-serif', fontSize: '14px', fontStyle: 'bold', color: '#f7fafc',
            backgroundColor: '#6b46c1e6', padding: { x: 12, y: 5 },
        }).setOrigin(0.5, 0).setScrollFactor(0).setResolution(3).setDepth(3000)
        // Async-alive subtitle: an empty room (play-time) still tells its story instead of feeling dead.
        const story = this.recentVisits > 0
            ? `👋 ${this.recentVisits} passage${this.recentVisits > 1 ? 's' : ''} récent${this.recentVisits > 1 ? 's' : ''}`
            : (this.mine ? 'invite des potes ce soir !' : 'tranquille pour l\'instant')
        this.add.text(W / 2, 32, story, {
            fontFamily: 'sans-serif', fontSize: '10px', color: '#10151f', backgroundColor: '#f6e05ed8', padding: { x: 7, y: 3 },
        }).setOrigin(0.5, 0).setScrollFactor(0).setResolution(3).setDepth(3000)

        // --- Leaving the room ---
        // Primary exit: walk Caz onto the door tile (the D at the bottom of the ASCII) → back to the school,
        // like any real door. (The old top-left button was hidden behind the HUD gauge panel, so the room was a
        // dead end — a page refresh was the only way out.) A floating hint marks the door.
        const door = this.doorTile()
        const ge: any = this.gridEngine
        const sub = ge.positionChangeFinished().subscribe((o: { charId: string }) => {
            if (o.charId !== this.playerName) return
            const p = ge.getPosition(this.playerName)
            if (p.x === door.x && p.y === door.y) this.exitToSchool()
        })
        this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => sub.unsubscribe())
        this.add.text(door.x * 16 + 8, door.y * 16 - 4, '↓ sortir', {
            fontFamily: 'sans-serif', fontSize: '8px', fontStyle: 'bold', color: '#10151f',
            backgroundColor: '#f6e05ee8', padding: { x: 4, y: 2 },
        }).setOrigin(0.5, 1).setResolution(3).setDepth(900)

        // Fallback button — moved BELOW the HUD gauge panel (top-left, ~y98) so it's no longer hidden behind it.
        this.pinBtn(8, 104, 0, 0, '← Sortir', '#f6e05ee8', '#10151f', () => this.exitToSchool())
        // 💜 react + 📖 guestbook (bottom-left).
        this.pinBtn(8, this.scale.height - 8, 0, 1, '💜', '#e53e8ee8', '#ffffff', () => this.react())
        this.pinBtn(54, this.scale.height - 8, 0, 1, '📖 Livre d\'or', '#2b4c7ee8', '#f7fafc', () => this.toggleGuestbook())
    }

    // scene.start re-inits the campus cleanly (it was stopped on entry) → land back at the school.
    private exitToSchool(): void { this.scene.start(SceneName.Campus) }

    // The 'D' tile in the bedroom ASCII (walkable; solid:false in the indoors theme).
    private doorTile(): { x: number; y: number } {
        for (let y = 0; y < BEDROOM_ASCII.length; y++) {
            const x = BEDROOM_ASCII[y].indexOf('D')
            if (x >= 0) return { x, y }
        }
        return { x: 5, y: 8 }
    }

    private pinBtn(x: number, y: number, ox: number, oy: number, label: string, bg: string, fg: string, on: () => void): Phaser.GameObjects.Text {
        const t = this.add.text(x, y, label, {
            fontFamily: 'sans-serif', fontSize: '13px', fontStyle: 'bold', color: fg, backgroundColor: bg, padding: { x: 8, y: 5 },
        }).setOrigin(ox, oy).setScrollFactor(0).setResolution(3).setDepth(3000).setInteractive({ useHandCursor: true })
        t.on('pointerdown', (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => { ev?.stopPropagation?.(); on() })
        return t
    }

    private toast(msg: string): void {
        const t = this.add.text(this.scale.width / 2, this.scale.height / 2, msg, {
            fontFamily: 'sans-serif', fontSize: '14px', fontStyle: 'bold', color: '#10151f',
            backgroundColor: '#f6e05ef0', padding: { x: 12, y: 8 },
        }).setOrigin(0.5).setScrollFactor(0).setResolution(3).setDepth(4000)
        this.tweens.add({ targets: t, alpha: 0, y: t.y - 20, delay: 900, duration: 700, onComplete: () => t.destroy() })
    }

    private async react(): Promise<void> {
        try {
            const r = await fetch(ServerLink.httpBase + '/api/room/react', {
                method: 'POST', headers: { 'Content-Type': 'application/json', ...ServerLink.authHeaders() },
                body: JSON.stringify({ roomId: this.cfg.id, text: '💜' }),
            })
            this.toast((await r.json()).ok ? '💜 envoyé !' : 'réessaie dans un instant')
        } catch { this.toast('hors-ligne') }
    }

    private async toggleGuestbook(): Promise<void> {
        if (this.gbPanel) { this.gbPanel.destroy(); this.gbPanel = undefined; return }
        let rows: Array<{ visitorName: string; kind: string; text: string }> = []
        try { rows = (await (await fetch(`${ServerLink.httpBase}/api/room/guestbook?id=${encodeURIComponent(this.cfg.id)}`)).json()).guestbook || [] } catch { /* offline */ }
        const W = this.scale.width, H = this.scale.height
        const c = this.add.container(0, 0).setScrollFactor(0).setDepth(3800)
        this.gbPanel = c
        const bg = this.add.rectangle(0, 0, W, H, 0x0a0e16, 0.85).setOrigin(0).setInteractive()
        bg.on('pointerdown', () => { c.destroy(); this.gbPanel = undefined })
        c.add(bg)
        c.add(this.add.text(W / 2, 20, '📖 Livre d\'or', { fontFamily: 'sans-serif', fontSize: '16px', fontStyle: 'bold', color: '#fff' }).setOrigin(0.5).setResolution(2))
        let y = 50
        for (const e of rows.slice(0, 12)) {
            const icon = e.kind === 'visit' ? '👋' : '💜'
            c.add(this.add.text(16, y, `${icon} ${e.visitorName} — ${e.text}`, { fontFamily: 'sans-serif', fontSize: '12px', color: '#e2e8f0', wordWrap: { width: W - 32 } }).setResolution(2))
            y += 22
        }
        if (!rows.length) c.add(this.add.text(W / 2, H / 2, 'Personne n\'est encore passé', { fontFamily: 'sans-serif', fontSize: '13px', color: '#9fb3c8' }).setOrigin(0.5))
        c.add(this.add.text(W / 2, H - 16, 'tape pour fermer', { fontFamily: 'sans-serif', fontSize: '11px', color: '#6b7a8d' }).setOrigin(0.5))
    }
}
